import { Form, FormikProvider, useField, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Field,
	FieldErrorMessage,
	FieldType,
	GraphQLErrorMessage,
	Heading3,
	Input,
	Modal,
	Paragraph,
	SettingsRow,
	SettingsTable,
	useToasts,
} from '@treams/treams-ui';

import DetailsTableForm from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import validator from '../../../packages/kakapo-ui-services/factories/ValidatorFactory';

function CompetencyLevel(props: {
	index: number;
	level: graphql.CompetencyLevel;
	onDelete?: () => void;
}) {
	const name = `levels.${props.index}.title`;
	return (
		<SettingsRow onDelete={props.onDelete}>
			<Input.Localizable name={name} />
			<FieldErrorMessage name={name} />
		</SettingsRow>
	);
}

function CompetencyLevelSetConfigurator() {
	const { t } = useTranslation();

	const [{ value: count }] = useField<string>('count');
	const [, , { setValue: setLevels }] = useField<graphql.CreateCompetencyLevelInput[]>('levels');
	const { language } = useLocalize();

	useEffect(() => {
		const levels: graphql.CreateCompetencyLevelInput[] = [];

		for (let level = 0; level < Math.max(0, Math.min(10, parseInt(count))); level += 1) {
			levels.push({
				title: [
					{
						languageID: language.id,
						text: `${t('common.settings.competencyLevelSets.create.placeholder')} ${level + 1}`,
					},
				],
			});
		}

		setLevels(levels);
	}, [count, language.id, setLevels, t]);

	return (
		<>
			<Box mt={3}>
				<Paragraph>{t('common.settings.competencyLevelSets.create.explanation')} </Paragraph>
			</Box>
			<Box mt={3}>
				<Paragraph>{t('common.settings.competencyLevelSets.create.instruction')}</Paragraph>
			</Box>
			<Flex alignItems="center" mt={3}>
				<Paragraph>{t('common.settings.competencyLevelSets.create.createSetWith')}</Paragraph>
				<Box mx={3} flexShrink={0} flexGrow={0}>
					<Field small unspaced name="count" type={FieldType.Number} hideError />
				</Box>
				<Paragraph>{t('common.settings.competencyLevelSets.create.options')}</Paragraph>
			</Flex>
			<FieldErrorMessage name="count" />
		</>
	);
}

function CompetencyLevelSetEditor(props: {
	set?: graphql.CompetencyLevelSet;
	onSave?: (set: graphql.CompetencyLevelSet) => void;
	onCancel?: () => void;
}) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { language } = useLocalize();
	const [confirmed, setConfirmed] = useState(!!props.set);

	const formik = useFormik({
		initialValues: {
			title: props.set?.title ?? [],
			levels:
				props.set?.levels ??
				Array.from(new Array(5)).map(
					(level): graphql.CompetencyLevel => ({
						id: '__draft',
						title: [
							{
								languageID: language.id,
								text: `${t('common.settings.competencyLevelSets.create.placeholder')} ${level + 1}`,
							},
						],
					}),
				),
			count: (props.set?.levels.length ?? 5).toFixed(),
		},
		onSubmit: () => onUpdate(),
		validationSchema: validator.object({
			title: validator
				.array(
					validator.object({
						text: validator.string().min(1).max(256).required(),
					}),
				)
				.min(1),

			levels: validator.array(
				validator.object({
					title: validator
						.array(
							validator.object({
								text: validator.string().min(1).max(256).required(),
							}),
						)
						.min(1),
				}),
			),
			count: validator.number().min(1).max(10),
		}),
	});

	const [error, attempt, reset] = useErrorBoundary();

	const onUpdate = attempt(async () => {
		reset();

		if (props.set) {
			const { data } = await update({
				variables: {
					input: {
						id: props.set.id,
						title: formik.values.title.map((string) => ({
							languageID: string.languageID,
							text: string.text,
						})),
						levels: formik.values.levels.map((level) => ({
							id: level.id,
							title: level.title.map((string) => ({
								languageID: string.languageID,
								text: string.text,
							})),
						})),
					},
				},
			});

			props.onSave?.(
				data?.updateCompetencyLevelSet.competencyLevelSet as graphql.CompetencyLevelSet,
			);
		} else {
			const { data } = await create({
				variables: {
					input: {
						title: formik.values.title.map((string) => ({
							languageID: string.languageID,
							text: string.text,
						})),
						levels: formik.values.levels.map((level) => ({
							title: level.title.map((string) => ({
								languageID: string.languageID,
								text: string.text,
							})),
						})),
					},
				},
			});

			if (props.onSave) {
				props.onSave(
					data?.createCompetencyLevelSet.competencyLevelSet as graphql.CompetencyLevelSet,
				);
			} else if (data) {
				history.push(
					`/settings/competency-level-sets/${data.createCompetencyLevelSet.competencyLevelSet.id}`,
				);
			}
		}

		addToast(t('common.settings.competencyLevelSets.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const [create] = graphql.useCreateCompetencyLevelSetMutation();
	const [update] = graphql.useUpdateCompetencyLevelSetMutation();

	const history = useHistory();

	return (
		<>
			<Modal.Contents>
				<Box mb={2}>
					<Heading3 mb={0}>{t('common.settings.competencyLevelSets.edit.details')}</Heading3>
				</Box>
				<GraphQLErrorMessage error={error} showValidationDetails />
				<FormikProvider value={formik}>
					<Form>
						<DetailsTableForm
							formValues={[
								{
									label: t('common.settings.competencyLevelSets.edit.name'),
									name: 'title',
									type: DetailsTableFormValueTypes.Multilingual,
								},
							]}
						/>
					</Form>

					<Box mt={3}>
						<SettingsTable divided title={t('common.settings.competencyLevelSets.edit.levels')}>
							{props.set || confirmed ? (
								formik.values.levels.map((level, index) => (
									<CompetencyLevel
										key={level.id}
										index={index}
										level={level}
										onDelete={
											props.set
												? undefined
												: () => {
														const levels = formik.values.levels.slice();
														levels.splice(index, 1);
														formik.setFieldValue('levels', levels);
													}
										}
									/>
								))
							) : (
								<CompetencyLevelSetConfigurator />
							)}
						</SettingsTable>
					</Box>
				</FormikProvider>
			</Modal.Contents>

			<Modal.FormFooter
				saveTitle={confirmed ? t('common.save') : t('common.next')}
				onSave={() => {
					if (confirmed) {
						return formik.submitForm();
					} else {
						return setConfirmed(true);
					}
				}}
				onCancel={props.onCancel}
			/>
		</>
	);
}

export default CompetencyLevelSetEditor;
